import React, { useState, useEffect } from 'react';
import { TrendingUp, Plus, Trash2, Calculator, AlertCircle, Lightbulb } from 'lucide-react';
import { ProfitAnalyzer } from '@/lib/profit-analyzer';
import type { ProfitPeriod, ProfitAnalysisResult } from '@/types';
import { formatCurrency, formatNumber, getProfitMarginColor, getProfitMarginBgColor, generateId } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Input from '@/components/ui/input';
import Button from '@/components/ui/button';

export default function ProfitAnalyzerComponent() {
  const [sellingPrice, setSellingPrice] = useState(30);
  const [unitBaseCost, setUnitBaseCost] = useState(20);
  const [periods, setPeriods] = useState<ProfitPeriod[]>(() => [
    { id: generateId(), name: '第1个月', targetSales: 500, naturalOrderRate: 20, cpc: 1.0, cvr: 10 },
    { id: generateId(), name: '第2个月', targetSales: 600, naturalOrderRate: 25, cpc: 1.1, cvr: 10 },
    { id: generateId(), name: '第3个月', targetSales: 700, naturalOrderRate: 30, cpc: 1.2, cvr: 11 },
    { id: generateId(), name: '第4个月', targetSales: 900, naturalOrderRate: 35, cpc: 1.3, cvr: 11 },
    { id: generateId(), name: '第5个月', targetSales: 1200, naturalOrderRate: 40, cpc: 1.4, cvr: 12 }
  ]);
  const [results, setResults] = useState<ProfitAnalysisResult[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showOptimization, setShowOptimization] = useState(false);

  // 计算利润分析
  const calculateProfit = async () => {
    setIsCalculating(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const analysisResults = ProfitAnalyzer.analyzeMultiplePeriods(periods, sellingPrice, unitBaseCost);
      setResults(analysisResults);
    } catch (error) {
      console.error('计算错误:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  // 自动计算
  useEffect(() => {
    if (periods.length > 0 && sellingPrice > 0 && unitBaseCost >= 0) {
      const timer = setTimeout(() => {
        const analysisResults = ProfitAnalyzer.analyzeMultiplePeriods(periods, sellingPrice, unitBaseCost);
        setResults(analysisResults);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [periods, sellingPrice, unitBaseCost]);

  // 添加期间
  const addPeriod = () => {
    const newPeriod: ProfitPeriod = {
      id: generateId(),
      name: `第${periods.length + 1}个月`,
      targetSales: 1000,
      naturalOrderRate: 30,
      cpc: 1.5,
      cvr: 10
    };
    setPeriods([...periods, newPeriod]);
  };

  // 删除期间
  const removePeriod = (id: string) => {
    setPeriods(periods.filter(p => p.id !== id));
  };

  // 更新期间
  const updatePeriod = (id: string, field: keyof ProfitPeriod, value: string | number) => {
    setPeriods(periods.map(period =>
      period.id === id
        ? { ...period, [field]: field === 'name' ? value : parseFloat(value as string) || 0 }
        : period
    ));
  };

  // 清空所有期间
  const clearAllPeriods = () => {
    setPeriods([]);
    setResults([]);
  };

  // 获取统计数据
  const summary = results.length > 0 ? ProfitAnalyzer.calculateSummary(results) : null;
  const breakEvenAnalysis = results.length > 0 ?
    ProfitAnalyzer.calculateBreakEven(periods, sellingPrice, unitBaseCost) : null;
  const optimizationSuggestions = results.length > 0 ?
    ProfitAnalyzer.generateOptimizationSuggestions(results) : [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calculator className="w-5 h-5 text-amazon-blue" />
            <span>利润预测分析器</span>
          </CardTitle>
          <CardDescription>
            基于广告投放数据的月度利润预测和盈亏平衡分析
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 基础参数 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="销售定价 (USD)"
                type="number"
                step="0.01"
                value={sellingPrice}
                onChange={(e) => setSellingPrice(parseFloat(e.target.value) || 0)}
              />
              <p className="text-xs text-gray-500 mt-1">产品销售价格</p>
            </div>
            <div>
              <Input
                label="单位成本 (USD)"
                type="number"
                step="0.01"
                value={unitBaseCost}
                onChange={(e) => setUnitBaseCost(parseFloat(e.target.value) || 0)}
              />
              <p className="text-xs text-gray-500 mt-1">不包括广告成本的单位成本</p>
            </div>
          </div>

          {/* 期间数据表格 */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">月度推广数据</h3>
              <div className="space-x-2">
                <Button onClick={addPeriod} size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  添加期间
                </Button>
                <Button onClick={clearAllPeriods} variant="secondary" size="sm">
                  <Trash2 className="w-4 h-4 mr-1" />
                  清空
                </Button>
              </div>
            </div>

            {periods.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-900">期间</th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-900">目标销量</th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-900">自然单率(%)</th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-900">CPC($)</th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-900">CVR(%)</th>
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-900">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map((period) => (
                      <tr key={period.id} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-4 py-2">
                          <input
                            type="text"
                            value={period.name}
                            onChange={(e) => updatePeriod(period.id, 'name', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-amazon-blue/20"
                          />
                        </td>
                        <td className="border border-gray-200 px-4 py-2">
                          <input
                            type="number"
                            value={period.targetSales}
                            onChange={(e) => updatePeriod(period.id, 'targetSales', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-amazon-blue/20"
                          />
                        </td>
                        <td className="border border-gray-200 px-4 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={period.naturalOrderRate}
                            onChange={(e) => updatePeriod(period.id, 'naturalOrderRate', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-amazon-blue/20"
                          />
                        </td>
                        <td className="border border-gray-200 px-4 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={period.cpc}
                            onChange={(e) => updatePeriod(period.id, 'cpc', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-amazon-blue/20"
                          />
                        </td>
                        <td className="border border-gray-200 px-4 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={period.cvr}
                            onChange={(e) => updatePeriod(period.id, 'cvr', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-amazon-blue/20"
                          />
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-center">
                          <Button
                            onClick={() => removePeriod(period.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calculator className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>请添加期间数据开始分析</p>
              </div>
            )}
          </div>

          <Button onClick={calculateProfit} loading={isCalculating} className="w-full" size="lg">
            计算利润分析
          </Button>
        </CardContent>
      </Card>

      {/* 分析结果 */}
      {results.length > 0 && (
        <div className="animate-fade-in space-y-6">
          {/* 总体统计 */}
          {summary && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5 text-amazon-blue" />
                  <span>总体分析</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-2xl font-bold text-blue-700">
                      {formatCurrency(summary.totalRevenue)}
                    </div>
                    <div className="text-sm text-blue-600 mt-1">总收入</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-2xl font-bold text-gray-700">
                      {formatCurrency(summary.totalCost)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">总成本</div>
                  </div>
                  <div className={`text-center p-4 rounded-lg border ${getProfitMarginBgColor(summary.averageMargin)}`}>
                    <div className={`text-2xl font-bold ${getProfitMarginColor(summary.averageMargin)}`}>
                      {formatCurrency(summary.totalProfit)}
                    </div>
                    <div className="text-sm mt-1">总利润</div>
                  </div>
                  <div className={`text-center p-4 rounded-lg border ${getProfitMarginBgColor(summary.averageMargin)}`}>
                    <div className={`text-2xl font-bold ${getProfitMarginColor(summary.averageMargin)}`}>
                      {formatNumber(summary.averageMargin, { decimals: 1, style: 'percent' })}
                    </div>
                    <div className="text-sm mt-1">平均利润率</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="text-xl font-bold text-purple-700">
                      {summary.totalOrders.toLocaleString()}
                    </div>
                    <div className="text-sm text-purple-600 mt-1">总订单数</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="text-xl font-bold text-orange-700">
                      {formatCurrency(summary.totalAdCost)}
                    </div>
                    <div className="text-sm text-orange-600 mt-1">总广告花费</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-xl font-bold text-green-700">
                      {formatCurrency(summary.averageCpa)}
                    </div>
                    <div className="text-sm text-green-600 mt-1">平均CPA</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className={`text-xl font-bold ${summary.profitGrowthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {summary.profitGrowthRate >= 0 ? '+' : ''}{summary.profitGrowthRate.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600 mt-1">利润增长率</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 详细期间结果 */}
          <Card>
            <CardHeader>
              <CardTitle>期间详细分析</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-900">期间</th>
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-900">目标销量</th>
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-900">自然单率</th>
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-900">广告单率</th>
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-900">CPA</th>
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-900">营收</th>
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-900">总成本</th>
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-900">利润</th>
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-900">利润率</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-4 py-2 font-medium">{result.period.name}</td>
                        <td className="border border-gray-200 px-4 py-2 text-center">{result.period.targetSales}</td>
                        <td className="border border-gray-200 px-4 py-2 text-center">{result.period.naturalOrderRate}%</td>
                        <td className="border border-gray-200 px-4 py-2 text-center">{result.adOrderRate.toFixed(1)}%</td>
                        <td className="border border-gray-200 px-4 py-2 text-center">{formatCurrency(result.adCpa)}</td>
                        <td className="border border-gray-200 px-4 py-2 text-center">{formatCurrency(result.revenue)}</td>
                        <td className="border border-gray-200 px-4 py-2 text-center">{formatCurrency(result.totalCost)}</td>
                        <td className={`border border-gray-200 px-4 py-2 text-center font-medium ${getProfitMarginColor(result.netProfitMargin)}`}>
                          {formatCurrency(result.profit)}
                        </td>
                        <td className={`border border-gray-200 px-4 py-2 text-center font-medium ${getProfitMarginColor(result.netProfitMargin)}`}>
                          {formatNumber(result.netProfitMargin, { decimals: 1, style: 'percent' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* 盈亏平衡分析 */}
          {breakEvenAnalysis && breakEvenAnalysis.breakEvenPeriod > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  <span>盈亏平衡分析</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="text-xl font-bold text-orange-700">
                      第{breakEvenAnalysis.breakEvenPeriod}个月
                    </div>
                    <div className="text-sm text-orange-600 mt-1">盈亏平衡期间</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-xl font-bold text-green-700">
                      {breakEvenAnalysis.breakEvenSales}
                    </div>
                    <div className="text-sm text-green-600 mt-1">当月销量</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-xl font-bold text-blue-700">
                      {formatCurrency(breakEvenAnalysis.breakEvenAdSpend)}
                    </div>
                    <div className="text-sm text-blue-600 mt-1">当月广告花费</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 优化建议 */}
          {optimizationSuggestions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  <span>优化建议</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {optimizationSuggestions.map((suggestion, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${
                      suggestion.priority === 'high' ? 'bg-red-50 border-red-200' :
                      suggestion.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          suggestion.priority === 'high' ? 'bg-red-500' :
                          suggestion.priority === 'medium' ? 'bg-yellow-500' :
                          'bg-blue-500'
                        }`}></div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-800">{suggestion.suggestion}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            类型: {suggestion.type === 'pricing' ? '定价' :
                                  suggestion.type === 'advertising' ? '广告' : '转化'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}